<template>
  <q-card
    v-bind="$attrs"
    :class="{ 'q-pa-lg': !dense }"
    :style="{
      'min-width': $q.screen.gt.sm && !kioskLogin ? '500px' : '300px',
      'max-width': $q.screen.gt.sm && !kioskLogin ? '500px' :undefined
    }"
    data-test-id="account-login-card"
  >
    <q-form
      ref="formRef"
      @submit.prevent="login"
    >
      <div class="column">
        <slot name="header">
          <div class="text-h6 q-mb-lg">
            {{ $kiosk ? $t('user.actions.find') : $t('auth.actions.login') }}
          </div>
        </slot>
        <login-help-banner v-if="$consumer" />
        <q-input
          id="email"
          v-model.trim="user.email"
          class="col"
          :class="{
            'q-pb-sm': $q.screen.lt.sm,
            [classes]: $kiosk
          }"
          type="email"
          :color="$consumer ? 'dark' : undefined"
          :bg-color="$consumer ? 'white' : undefined"
          :dark="!$consumer && $q.dark.isActive"
          :label="$t('form.labels.emailAddress')"
          :rules="[
            val => required.$validator(val) || $t('form.errors.required'),
            val => email.$validator(val.toLowerCase()) || $t('form.errors.emailNotValid')
          ]"
          lazy-rules
          :autofocus="!user.email && autofocusEnabled"
          bottom-slots
          :hide-bottom-space="$q.screen.lt.sm"
          data-test-id="email-input"
        />
        <q-input
          id="password"
          v-model="user.password"
          class="col"
          :class="{
            'q-pb-sm': $q.screen.lt.sm,
            [classes]: $kiosk
          }"
          type="password"
          :label="$t('form.labels.password')"
          :rules="[val => required.$validator(val) || $t('form.errors.required')]"
          :color="$consumer ? 'dark' : undefined"
          :bg-color="$consumer ? 'white' : undefined"
          :dark="!$consumer && $q.dark.isActive"
          lazy-rules
          bottom-slots
          :hide-bottom-space="$q.screen.lt.sm"
          :autofocus="!!user.email"
          data-test-id="password-input"
          @keyup.enter="login"
        />
        <q-btn
          class="full-width q-pb-md text-capitalize"
          :class="{
            [classes]: $kiosk,
            'rounded-borders-8': !$register
          }"
          size="lg"
          :padding="buttonPadding"
          color="primary"
          :text-color="buttonTextColor"
          :loading="loading"
          :label="$kiosk ? $t('user.actions.find') : $t('auth.actions.login')"
          data-test-id="login-submit-button"
          type="submit"
        />
        <div
          v-if="!hideLoginButtons || !hideForgotPasswordButton"
          class="row items-center"
          :class="{
            'justify-center': hideRegisterButton,
            'justify-between': !hideRegisterButton,
            'q-mt-lg': !$q.screen.lt.sm,
            'q-mt-xl': $q.screen.gt.xs
          }"
        >
          <q-btn
            v-if="!hideRegisterButton"
            flat
            color="primary"
            :label="$t('auth.actions.signup')"
            class="text-capitalize"
            data-test-id="register-link"
            @click="$kiosk && $emit('register') || $router.push('/register')"
          />
          <q-btn
            v-if="!hideForgotPasswordButton"
            type="button"
            flat
            color="secondary"
            :label="$t('auth.actions.goToForgotPassword')"
            class="text-capitalize"
            to="/password/forgot"
            data-test-id="forgot-password-link"
          />
        </div>
        <slot name="default" />
      </div>
    </q-form>
  </q-card>
</template>

<script>
import { email, required } from '@vuelidate/validators'
import { Dialog } from 'quasar'
import Model from 'src/models/model'
import User from 'src/models/user/User'
import FormValidationMixin from 'src/modules/admin/mixins/FormValidationMixin'
import LoginHelpBanner from 'src/modules/checkout/components/LoginHelpBanner'
export default {
  name: 'Login',
  components: {
    LoginHelpBanner
  },
  mixins: [FormValidationMixin],
  configuration: ['currentSite'],
  props: {
    kioskLogin: {
      type: Boolean,
      default: false,
      required: false
    },
    consumerLogin: {
      type: Boolean,
      default: false,
      required: false
    },
    postAuthPath: {
      type: String,
      default: '/',
      required: false
    },
    hideRegisterButton: {
      type: Boolean,
      default: false,
      required: false
    },
    hideForgotPasswordButton: {
      type: Boolean,
      default: false,
      required: false
    },
    dense: {
      type: Boolean,
      default: false,
      required: false
    },
    autofocusEnabled: {
      type: Boolean,
      default: true,
      required: false
    },
    buttonPadding: {
      type: String,
      default: undefined,
      required: false
    }
  },
  emits: ['register', 'login-success'],
  data () {
    return {
      user: null,
      loading: false
    }
  },
  computed: {
    buttonTextColor () {
      return this.$isColorDark(this.currentSite.primaryColor) ? 'white' : 'black'
    },
    classes () {
      const classes = []
      if (this.$kiosk) {
        classes.push('thick-border kiosk')
      }
      if (this.$isLandscapeView()) {
        classes.push('landscape')
      } else {
        classes.push('portrait')
      }
      return classes.join(' ')
    }
  },
  created () {
    _.extend(this, { email, required })
  },
  beforeMount () {
    this.user = new User()
  },
  mounted () {
    const authData = this.$auth.getAuthData()
    if (authData.email) {
      this.user.email = authData.email
    }
  },
  unmounted () {
    this.user.destroy()
  },
  methods: {
    login () {
      this.validateForm(async () => {
        this.loading = true

        const { data, errors } = await this.user.logIn(this.kioskLogin)

        if ((errors && errors.length) || (data.errors && data.errors.length)) {
          if (errors[0].code === 'no_password_set') {
            Dialog.create({
              title: 'Account Exists',
              message: 'Your account exists but no password has been set. We will send you an email you can use to set your password.'
            }).onDismiss(() => {
              if (!this.kioskLogin) {
                this.$router.push('/password/claim-account')
              }
            })

            this.loading = false
            return
          } else {
            this.$mh.showErrors(errors || data.errors)
            this.loading = false
            return
          }
        }

        if (this.kioskLogin) {
          this.$emit('login-success', data?.user?.id)
          this.loading = false
          return
        }

        if (data.user) {
          await Model.configuration.validate()
          await Model.context.fetch(true)
          this.$auth.updateCableSubscriptions()

          // For consumer login: emit success event and return early to prevent automatic redirect
          // This allows the parent component to handle navigation instead of the auth system
          if (this.consumerLogin) {
            this.$emit('login-success', data?.user?.id)
            return
          }

          // Otherwise, navigate to the post auth path
          this.$auth.navigatePostAuth(this.postAuthPath)
        }

        this.loading = false
      })
    }
  }
}
</script>
<style lang="scss" scoped>
/**
 * Kiosk specific styles
 */
.kiosk.landscape {
  font-size: 2vw;
}
.kiosk.portrait {
  font-size: 2vh;
}
.q-btn.kiosk.landscape {
  font-size: 2vw;
}
.q-btn.kiosk.portrait {
  font-size: 2vh;
}

.q-btn-group.kiosk.landscape {
  padding: 0.4vw;
}
.q-btn-group.kiosk.portrait {
  padding: 0.4vh;
}

:deep(.q-field--outlined.thick-border .q-field__control:before) {
    border-width: 3px;
  }
:deep(.q-btn-group .text-body1){
  font-size: 1em;
}
:deep(.kiosk.portrait .q-field__control){
  height: 6vh;
}
:deep(.kiosk.landscape .q-field__control){
  height: 5vw;
}
:deep(.kiosk.portrait.q-field--labeled .q-field__native){
  min-height: 6vh;
  font-size: 2vh;
}
:deep(.kiosk.landscape.q-field--labeled .q-field__native){
  min-height: 5vw;
  font-size: 2vw;
}

</style>
